import React, { useState } from 'react';
import {
    HiShoppingBag,
    HiChevronDown,
    HiEye,
    HiCalendarDays,
    HiHashtag,
    HiCircleStack,
    HiCreditCard,
} from 'react-icons/hi2';

const OrdersTab = ({
    orders,
    loading,
    error,
    formatDate,
    getStatusClass,
    getItemTypeClass,
    getItemTypeLabel,
    onViewInvoice,
}) => {
    const [expandedOrders, setExpandedOrders] = useState(
        orders.reduce((acc, order) => ({ ...acc, [order.order_id]: true }), {})
    );

    const toggleExpand = (orderId) => {
        setExpandedOrders((prev) => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    if (loading) {
        return (
            <div className="my-accounts-content">
                <p className="my-accounts-loading">Loading orders...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="my-accounts-content">
                <div className="my-accounts-empty-state">
                    <HiShoppingBag className="my-accounts-empty-icon" />
                    <h3 className="my-accounts-empty-title">Unable to load</h3>
                    <p className="my-accounts-empty-message">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="my-accounts-content">
            {orders.length === 0 ? (
                <div className="my-accounts-empty-state">
                    <HiShoppingBag className="my-accounts-empty-icon" />
                    <h3 className="my-accounts-empty-title">No orders yet</h3>
                    <p className="my-accounts-empty-message">
                        Your purchase history will appear here once you make a purchase.
                    </p>
                </div>
            ) : (
                orders.map((order) => {
                    const currencySymbol = order.currency_symbol || order.invoices?.[0]?.currency_symbol || '';
                    const currencyName = order.currency_name || order.invoices?.[0]?.currency_name || '';
                    const currencyDisplay = currencySymbol || currencyName;
                    const orderSubtotal = parseFloat(order.subtotal ?? order.sub ?? 0);
                    const orderDiscount = parseFloat(order.discount_amount || 0);
                    const orderTotal = parseFloat(order.total ?? order.total_price ?? 0);

                    return (
                        <div key={order.order_id} className="my-accounts-order-card">
                            <div
                                className="my-accounts-order-header"
                                onClick={() => toggleExpand(order.order_id)}
                            >
                                <div className="my-accounts-order-header-left">
                                    <div className="my-accounts-order-meta">
                                        <span className="my-accounts-order-id">
                                            <HiHashtag className="my-accounts-inline-icon" />
                                            Order {order.order_id}
                                        </span>
                                        <span className={`my-accounts-status-badge ${getStatusClass(order.status)}`}>
                                            {order.status}
                                        </span>
                                    </div>
                                    <span className="my-accounts-order-date">
                                        <HiCalendarDays className="my-accounts-inline-icon" />
                                        Updated {formatDate(order.updated_at)}
                                    </span>
                                    {order.full_billing_address && (
                                        <span className="my-accounts-order-address-chip">
                                            <span className="my-accounts-order-address-label">Billing Address</span>
                                            <span className="my-accounts-order-address-value">{order.full_billing_address}</span>
                                        </span>
                                    )}
                                </div>

                                <div className="my-accounts-order-header-right">
                                    <div className="my-accounts-order-total-wrap">
                                        {orderSubtotal > 0 && (
                                            <span className="my-accounts-order-amount-muted">
                                                Subtotal {currencyDisplay} {orderSubtotal.toFixed(2)}
                                            </span>
                                        )}
                                        {orderDiscount > 0 && (
                                            <span className="my-accounts-order-amount-discount">
                                                Discount {currencyDisplay} {orderDiscount.toFixed(2)}
                                            </span>
                                        )}
                                        <span className="my-accounts-order-total">
                                            Total {currencyDisplay} {orderTotal.toFixed(2)}
                                        </span>
                                    </div>
                                    <HiChevronDown
                                        className={`my-accounts-expand-icon ${expandedOrders[order.order_id] ? 'expanded' : ''}`}
                                    />
                                </div>
                            </div>

                            {expandedOrders[order.order_id] && (
                                <div className="my-accounts-order-body">
                                    <h4 className="my-accounts-section-title">
                                        <HiShoppingBag className="my-accounts-section-icon" />
                                        Order Items ({order.item_count})
                                    </h4>

                                    <div className="container-fluid px-0">
                                        <div className="row g-3">
                                            {order.order_items.map((item) => (
                                                <div key={item.order_item_id} className="col-12">
                                                    <div className="my-accounts-line-card">
                                                        <div className="row g-3 align-items-start">
                                                            <div className="col-12 col-lg-5">
                                                                <div className="my-accounts-line-item-name">{item.item_name}</div>
                                                                {item.user_name && (
                                                                    <div className="my-accounts-line-user">{item.user_name}</div>
                                                                )}
                                                                <span className={`my-accounts-item-type-badge ${getItemTypeClass(item.item_type)}`}>
                                                                    {getItemTypeLabel(item.item_type)}
                                                                </span>
                                                            </div>

                                                            <div className="col-6 col-md-4 col-lg-3">
                                                                <div className="my-accounts-mini-stat">
                                                                    <span className="my-accounts-mini-stat-label">
                                                                        <HiCircleStack className="my-accounts-inline-icon" />
                                                                        Quantity
                                                                    </span>
                                                                    <span className="my-accounts-mini-stat-value">{item.quantity}</span>
                                                                </div>
                                                                <div className="my-accounts-mini-stat">
                                                                    <span className="my-accounts-mini-stat-label">Credits</span>
                                                                    <span className="my-accounts-mini-stat-value">
                                                                        {item.item_type === 'credit_package' ? (item.credits || '-') : '-'}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="col-6 col-md-4 col-lg-2">
                                                                <div className="my-accounts-mini-stat">
                                                                    <span className="my-accounts-mini-stat-label">Unit Price</span>
                                                                    <span className="my-accounts-mini-stat-value">
                                                                        {currencyDisplay} {parseFloat(item.unit_price || 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                                <div className="my-accounts-mini-stat">
                                                                    <span className="my-accounts-mini-stat-label">Unit Discount</span>
                                                                    <span className="my-accounts-mini-stat-value my-accounts-mini-stat-discount">
                                                                        {currencyDisplay} {parseFloat(item.unit_discount || 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="col-12 col-md-4 col-lg-2">
                                                                <div className="my-accounts-line-total-card">
                                                                   <span className="my-accounts-mini-stat-value">
                                                                        Sub Total Value {currencyDisplay} {(parseFloat(item.unit_price || 0) * parseFloat(item.quantity || 0)).toFixed(2)}
                                                                    </span>
                                                                    <span className="my-accounts-line-total-sub">
                                                                        Total Discount {currencyDisplay} {parseFloat(item.total_discount || 0).toFixed(2)}
                                                                    </span>
                                                                     <span className="my-accounts-mini-stat-label">
                                                                        <HiCreditCard className="my-accounts-inline-icon" />
                                                                        Total
                                                                    </span>
                                                                    <span className="my-accounts-line-total-value">
                                                                        {currencyDisplay} {parseFloat(item.total_price || 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="my-accounts-order-summary">
                                        <div className="row g-3">
                                            <div className="col-12 col-md-4">
                                                <div className="my-accounts-summary-block">
                                                    <span className="my-accounts-summary-label">Subtotal</span>
                                                    <span className="my-accounts-summary-value">
                                                        {currencyDisplay} {orderSubtotal.toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <div className="my-accounts-summary-block">
                                                    <span className="my-accounts-summary-label">Discount</span>
                                                    <span className="my-accounts-summary-value my-accounts-summary-discount">
                                                        {currencyDisplay} {orderDiscount.toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <div className="my-accounts-summary-block my-accounts-summary-block-total">
                                                    <span className="my-accounts-summary-label">Total</span>
                                                    <span className="my-accounts-summary-value">
                                                        {currencyDisplay} {orderTotal.toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {order.status?.toLowerCase() === 'completed' && (
                                        <div className="my-accounts-action-row">
                                            <button
                                                onClick={() => onViewInvoice && onViewInvoice(order.order_id)}
                                                className="my-accounts-view-invoice-btn"
                                            >
                                                <HiEye className="my-accounts-download-icon" />
                                                View Invoice
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default OrdersTab;
